import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollToTop — routing-aware scroll manager for the SPA.
 *
 * Behaviour by navigation type (React Router v7 `useNavigationType()`):
 *
 *   PUSH     — forward navigation (tapped into a set / card). Scroll
 *              instantly to the top of the new document.
 *   REPLACE  — canonical-URL slug rewrites (SetDetail / CardDetail).
 *              Do NOT scroll — the user is on the same "page" and only
 *              its URL is being tidied.
 *   POP      — Back / Forward / mobile swipe-back. Restore the scroll
 *              position that was recorded for this location.key when
 *              it was last visible, so users resume where they left off
 *              on long lists (sets grid, cards grid).
 *
 * Position persistence:
 *   Every user scroll writes `window.scrollY` into `sessionStorage`
 *   keyed by the current `location.key`. sessionStorage is scoped to
 *   the tab and survives client-side navigations, matching the "resume
 *   where I was" mental model. Cleared when the tab closes.
 *
 * Async-content restore (the hard part):
 *   Lists fetch on mount, so at the moment we return from a POP the
 *   document may only be one viewport tall. A single scrollTo() would
 *   silently clamp to maxScroll and land at 0.
 *   The restore runs a rAF loop for ~2s, PLUS four deferred timeout
 *   retries at 250 / 750 / 1500 / 3500 ms, each firing scrollTo only
 *   when the document has grown tall enough to hold the target. All
 *   retries are cancelled on the next route change.
 *
 * Browser `history.scrollRestoration` is set to `"manual"` at module
 * load (see below) so the browser's own restore doesn't fight ours by
 * firing on popstate before the SPA data has hydrated.
 */

const SCROLL_KEY_PREFIX = "cc:scroll:";
const RAF_BUDGET_MS = 2000;
const BACKUP_RETRY_DELAYS_MS = [250, 750, 1500, 3500];

// Take manual scroll control as early as possible — running here (at
// module import time) beats popstate on the very first Back tap after
// a hard reload.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

export default function ScrollToTop() {
  const { key } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cleanups = [];

    if (navType === "POP") {
      const saved = window.sessionStorage.getItem(SCROLL_KEY_PREFIX + key);
      const target = saved ? parseInt(saved, 10) || 0 : 0;
      if (target > 0) {
        let done = false;
        const tryScroll = () => {
          if (done) return true;
          const maxScroll =
            document.documentElement.scrollHeight - window.innerHeight;
          if (maxScroll >= target) {
            window.scrollTo({ top: target, left: 0, behavior: "instant" });
            done = true;
            return true;
          }
          return false;
        };
        const rafDeadline = performance.now() + RAF_BUDGET_MS;
        const rafLoop = () => {
          if (done) return;
          if (tryScroll()) return;
          if (performance.now() > rafDeadline) return;
          window.requestAnimationFrame(rafLoop);
        };
        window.requestAnimationFrame(rafLoop);
        const timers = BACKUP_RETRY_DELAYS_MS.map((ms) =>
          window.setTimeout(tryScroll, ms),
        );
        cleanups.push(() => {
          done = true;
          timers.forEach((t) => window.clearTimeout(t));
        });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    } else if (navType === "PUSH") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    // REPLACE: intentional no-op.

    // Persist the CURRENT key's scroll position so the next POP back
    // to this key can resume exactly where we left off.
    const onScroll = () => {
      try {
        window.sessionStorage.setItem(
          SCROLL_KEY_PREFIX + key,
          String(window.scrollY),
        );
      } catch (err) {
        // sessionStorage may throw in private-mode / quota-exceeded —
        // non-fatal: scroll restore for this key will fall back to 0
        // (page top) on the next POP. Log once so the operator can
        // trace it without breaking the scroll experience.
        console.warn(
          "[ScrollToTop] sessionStorage.setItem failed; scroll restore for",
          SCROLL_KEY_PREFIX + key,
          "will land at top on next POP:",
          err && err.message ? err.message : err,
        );
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", onScroll));

    return () => cleanups.forEach((fn) => fn());
  }, [key, navType]);

  return null;
}
